import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CreateUserSchema, UpdateUserSchema, UserRole, DepartmentPosition } from "@arna-med/shared";
import { useSession } from "@/lib/auth-client";
import { getLocalizedName } from "@/lib/utils";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, X, Loader2, Search, Mail, UserPlus, MoreHorizontal, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Pagination } from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useActivateUser,
  useDeactivateUser,
  useResendInvitation,
  useManagerDoctors,
  useAssignDoctor,
  useUnassignDoctor,
  useDoctors,
} from "@/hooks/api/useUsers";
import { useSpecialities } from "@/hooks/api/useSpecialities";
import { useDepartments } from "@/hooks/api/useDepartments";

const PAGE_SIZE = 10;

type CreateUserForm = z.infer<typeof CreateUserSchema>;
type UpdateUserForm = z.infer<typeof UpdateUserSchema>;

function AssignDoctorsDialog({ managerId, managerName, open, onOpenChange }: {
  managerId: string;
  managerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data: assignedDoctors = [], isLoading: loadingAssigned } = useManagerDoctors(managerId, { enabled: open });
  const { data: allDoctors = [], isLoading: loadingAll } = useDoctors({ enabled: open });
  const assignMutation = useAssignDoctor(managerId);
  const unassignMutation = useUnassignDoctor(managerId);

  const assignedIds = new Set(assignedDoctors.map((d) => d.id));

  const toggle = (doctorId: string) => {
    if (assignedIds.has(doctorId)) {
      unassignMutation.mutate(doctorId, {
        onError: () => toast.error(t("common.error")),
      });
    } else {
      assignMutation.mutate(doctorId, {
        onError: () => toast.error(t("common.error")),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("admin.users.assignDoctors")} — {managerName}</DialogTitle>
        </DialogHeader>
        {loadingAssigned || loadingAll ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : allDoctors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t("admin.users.noAssignedDoctors")}</p>
        ) : (
          <div className="overflow-y-auto space-y-1 flex-1">
            {allDoctors.map((doctor) => {
              const isAssigned = assignedIds.has(doctor.id);
              const isPending = assignMutation.isPending || unassignMutation.isPending;
              return (
                <div
                  key={doctor.id}
                  className="flex items-center justify-between p-2 rounded hover:bg-muted/50"
                >
                  <div>
                    <p className="text-sm font-medium">{doctor.name}</p>
                    <p className="text-xs text-muted-foreground">{doctor.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={isAssigned ? "secondary" : "outline"}
                    disabled={isPending}
                    onClick={() => toggle(doctor.id)}
                  >
                    {isAssigned ? t("admin.users.unassign") : t("admin.users.assign")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { t, i18n } = useTranslation();
  const { data: session } = useSession();
  const currentRole = (session?.user as { role?: string } | undefined)?.role as
    | UserRole
    | undefined;
  const isOwner = currentRole === UserRole.Admin;
  const isManager = currentRole === UserRole.Manager;
  const canDelete = (targetRole: UserRole) =>
    isOwner || (isManager && targetRole === UserRole.Doctor);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [assignDialogManager, setAssignDialogManager] = useState<{ id: string; name: string } | null>(null);
  const [form, setForm] = useState<CreateUserForm>({
    email: "",
    name: "",
    role: UserRole.Doctor,
    departmentId: null,
    departmentPosition: null,
  });
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserForm>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: response, isLoading } = useUsers({ page, limit: PAGE_SIZE, search: search || undefined });
  const { data: specialities = [] } = useSpecialities();
  const { data: departments = [] } = useDepartments();
  const createMutation = useCreateUser();
  const deleteMutation = useDeleteUser();
  const deactivateMutation = useDeactivateUser();
  const activateMutation = useActivateUser();
  const updateMutation = useUpdateUser();
  const resendInvitationMutation = useResendInvitation();

  const users = response?.data ?? [];
  const editingUser = users.find((u) => u.id === editingUserId);
  const assignableSpecialities = specialities.filter((s) => !s.isGeneral);

  const specialityName = (id: string | null | undefined) => {
    if (!id) return null;
    const s = specialities.find((sp) => sp.id === id);
    if (!s) return null;
    return getLocalizedName(s, i18n.language);
  };

  return (
    <Layout>
      <h1 className="text-2xl font-bold mb-6">{t("admin.users.title")}</h1>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("admin.users.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Button onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("admin.users.addUser")}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">
              {t("admin.users.addUser")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const parsed = CreateUserSchema.safeParse(form);
                if (parsed.success)
                  createMutation.mutate(parsed.data, {
                    onSuccess: () => {
                      setShowForm(false);
                      setForm({ email: "", name: "", role: UserRole.Doctor, departmentId: null, departmentPosition: null });
                    },
                    onError: (err: unknown) => {
                      const status = (err as { response?: { status?: number } })?.response?.status;
                      if (status === 409) {
                        toast.error(t("admin.users.emailAlreadyExists"));
                      }
                    },
                  });
              }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-2">
                <Label>{t("admin.users.fullName")}</Label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t("auth.email")}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      role: e.target.value as UserRole,
                      specialityId: undefined,
                    }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value={UserRole.Doctor}>
                    {t("admin.users.role.doctor")}
                  </option>
                  {isOwner && (
                    <>
                      <option value={UserRole.Manager}>
                        {t("admin.users.role.manager")}
                      </option>
                      <option value={UserRole.Admin}>
                        {t("admin.users.role.admin")}
                      </option>
                      <option value={UserRole.Auditor}>
                        {t("admin.users.role.auditor")}
                      </option>
                    </>
                  )}
                </select>
              </div>
              {form.role === UserRole.Doctor && (
                <div className="space-y-2">
                  <Label>{t("admin.users.speciality")}</Label>
                  <select
                    value={form.specialityId ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        specialityId: e.target.value || undefined,
                      }))
                    }
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">{t("admin.users.noSpeciality")}</option>
                    {assignableSpecialities.map((s) => (
                      <option key={s.id} value={s.id}>
                        {getLocalizedName(s, i18n.language)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label>{t("departments.departmentLabel")}</Label>
                <select
                  value={form.departmentId ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, departmentId: e.target.value || null }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("departments.noDepartment")}</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t("departments.positionLabel")}</Label>
                <select
                  value={form.departmentPosition ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, departmentPosition: (e.target.value as DepartmentPosition) || null }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("departments.noPosition")}</option>
                  {Object.values(DepartmentPosition).map((pos) => (
                    <option key={pos} value={pos}>{t(`departments.positions.${pos}`)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("common.save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {editingUserId && editingUser && (
        <Card className="mb-6 border-primary">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {t("common.edit")} — {editingUser.name}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditingUserId(null);
                  setEditForm({});
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate(
                  { id: editingUserId, data: editForm },
                  {
                    onSuccess: () => {
                      setEditingUserId(null);
                      setEditForm({});
                    },
                  },
                );
              }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-2">
                <Label>{t("admin.users.fullName")}</Label>
                <Input
                  value={editForm.name ?? editingUser.name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("auth.email")}</Label>
                <Input
                  type="email"
                  value={editForm.email ?? editingUser.email}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <select
                  value={editForm.role ?? editingUser.role}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      role: e.target.value as UserRole,
                      specialityId: undefined,
                    }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value={UserRole.Doctor}>
                    {t("admin.users.role.doctor")}
                  </option>
                  {isOwner && (
                    <>
                      <option value={UserRole.Manager}>
                        {t("admin.users.role.manager")}
                      </option>
                      <option value={UserRole.Admin}>
                        {t("admin.users.role.admin")}
                      </option>
                      <option value={UserRole.Auditor}>
                        {t("admin.users.role.auditor")}
                      </option>
                    </>
                  )}
                </select>
              </div>
              {(editForm.role ?? editingUser.role) === UserRole.Doctor && (
                <div className="space-y-2">
                  <Label>{t("admin.users.speciality")}</Label>
                  <select
                    value={
                      editForm.specialityId !== undefined
                        ? (editForm.specialityId ?? "")
                        : (editingUser.specialityId ?? "")
                    }
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        specialityId: e.target.value || null,
                      }))
                    }
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">{t("admin.users.noSpeciality")}</option>
                    {assignableSpecialities.map((s) => (
                      <option key={s.id} value={s.id}>
                        {getLocalizedName(s, i18n.language)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label>{t("departments.departmentLabel")}</Label>
                <select
                  value={
                    editForm.departmentId !== undefined
                      ? (editForm.departmentId ?? "")
                      : ((editingUser as { departmentId?: string | null }).departmentId ?? "")
                  }
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, departmentId: e.target.value || null }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("departments.noDepartment")}</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t("departments.positionLabel")}</Label>
                <select
                  value={
                    editForm.departmentPosition !== undefined
                      ? (editForm.departmentPosition ?? "")
                      : ((editingUser as { departmentPosition?: string | null }).departmentPosition ?? "")
                  }
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, departmentPosition: (e.target.value as DepartmentPosition) || null }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("departments.noPosition")}</option>
                  {Object.values(DepartmentPosition).map((pos) => (
                    <option key={pos} value={pos}>{t(`departments.positions.${pos}`)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingUserId(null);
                    setEditForm({});
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("common.save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("admin.users.fullName")}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("auth.email")}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("admin.users.roleHeader")}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("admin.users.speciality")}
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className={`border-b last:border-0 hover:bg-muted/30 transition-colors${!u.isActive ? " opacity-60" : ""}`}
                  >
                    <td className="px-4 py-3 font-medium whitespace-nowrap">
                      {u.name}
                      {!u.isActive && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">
                          {t("common.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {u.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      <span className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground text-xs">
                        {t(`admin.users.role.${u.role}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {specialityName(u.specialityId) ?? "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="focus-visible:ring-0 focus-visible:ring-offset-0"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingUserId(u.id);
                              setEditForm({});
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                            {t("common.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              resendInvitationMutation.mutate(u.id, {
                                onSuccess: () => toast.success(t("admin.users.resendInvitationSuccess")),
                                onError: () => toast.error(t("admin.users.resendInvitationError")),
                              })
                            }
                          >
                            <Mail className="h-4 w-4" />
                            {t("admin.users.resendInvitation")}
                          </DropdownMenuItem>
                          {u.role === UserRole.Manager && isOwner && (
                            <DropdownMenuItem
                              onClick={() => setAssignDialogManager({ id: u.id, name: u.name })}
                            >
                              <UserPlus className="h-4 w-4" />
                              {t("admin.users.assignDoctors")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {u.isActive ? (
                            <DropdownMenuItem
                              onClick={() => deactivateMutation.mutate(u.id)}
                            >
                              {t("admin.users.deactivate")}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => activateMutation.mutate(u.id)}
                            >
                              {t("admin.users.activate")}
                            </DropdownMenuItem>
                          )}
                          {canDelete(u.role as UserRole) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setConfirmDeleteId(u.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                                {t("common.delete")}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={Math.ceil((response?.total ?? 0) / PAGE_SIZE)}
            totalItems={response?.total ?? 0}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}
        onConfirm={() => { deleteMutation.mutate(confirmDeleteId!); setConfirmDeleteId(null); }}
        isPending={deleteMutation.isPending}
      />
      {assignDialogManager && (
        <AssignDoctorsDialog
          managerId={assignDialogManager.id}
          managerName={assignDialogManager.name}
          open={true}
          onOpenChange={(open) => { if (!open) setAssignDialogManager(null); }}
        />
      )}
    </Layout>
  );
}
